import noop from 'lodash/noop';
import { useMemo } from 'react';

import { GuardianMarkAsRemovedCommand, GuardianSimple } from '@dreryk/egabinet-patient-frontend-api';
import { GuardianExternalIdentifier } from '@dreryk/egabinet-patient-published-language-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { createSingleInterpolatedTranslationKey, dateFormatter, useTranslate } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   dispatchSnackbarPositiveEvent,
   TableColumnConfiguration,
   TextPlaceholder,
   ValueRendererProps,
} from '@dreryk/react-components';

import { guardiansTranslationKeys } from '../../../../../translations';

import { ActionsCell } from './cells/ActionsCell/ActionsCell';
import { GuardianCell } from './cells/GuardianCell/GuardianCell';
import { PhoneNumberCell } from './cells/PhoneNumberCell/PhoneNumberCell';

export type GuardianMarkAsRemovedParameters = {
   guardianId: number;
   fullName: string;
};

export const useGuardianMarkAsRemovedCommand = (
   onGuardianDeleted: () => Promise<void>
): UseCqrsCommandResult<void, GuardianMarkAsRemovedParameters> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new GuardianMarkAsRemovedCommand();
         command.id = parameters.guardianId;
         return command;
      },
      {
         onSuccess: async (result, { fullName }) => {
            const message = createSingleInterpolatedTranslationKey(
               guardiansTranslationKeys.guardianRemoved,
               'fullName',
               fullName
            );
            dispatchSnackbarPositiveEvent(message);
            await onGuardianDeleted();
         },
      }
   );
};

// eslint-disable-next-line max-lines-per-function
export const useGuardiansTableColumnConfiguration = (
   onEdit: (guardianId: number, guardianIdentifier: GuardianExternalIdentifier) => void,
   onDelete: (variables: GuardianMarkAsRemovedParameters) => Promise<void> | void
): TableColumnConfiguration<GuardianSimple>[] => {
   const translate = useTranslate();
   // eslint-disable-next-line max-lines-per-function
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         guardian: '100%',
         phoneNumber: '216px',
         relation: '216px',
         statutoryRepresentative: '216px',
         creationDate: '180px',
         actions: '64px',
      });
      return [
         {
            name: 'guardian',
            accessor: noop,

            renderer: ({ bean }: ValueRendererProps<GuardianSimple, any>) => (
               <GuardianCell
                  firstName={bean.firstName}
                  lastName={bean.lastName}
                  guardianExternalIdentifier={bean.guardianExternalIdentifier}
               />
            ),
            label: guardiansTranslationKeys.guardian,
            width: columnsWidth.guardian,
            sortingDisabled: true,
         },
         {
            name: 'phoneNumber',
            accessor: noop,

            renderer: ({ bean }: ValueRendererProps<GuardianSimple, any>) => (
               <PhoneNumberCell phoneNumber={bean.phoneNumber} />
            ),
            label: guardiansTranslationKeys.phone,
            width: columnsWidth.phoneNumber,
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'relation',
            accessor: 'relation',

            renderer: ({ bean }: ValueRendererProps<GuardianSimple, any>) => (
               <>{translate(guardiansTranslationKeys.guardianRelation[bean.relation])}</>
            ),
            label: guardiansTranslationKeys.relation,
            width: columnsWidth.relation,
            sortingDisabled: true,
         },
         {
            name: 'statutoryRepresentative',
            accessor: 'statutoryRepresentative',

            renderer: ({ bean }: ValueRendererProps<GuardianSimple, any>) => (
               <>{translate(guardiansTranslationKeys[bean.statutoryRepresentative ? 'yes' : 'no'])}</>
            ),
            label: guardiansTranslationKeys.statutoryRepresentative,
            width: columnsWidth.statutoryRepresentative,
            sortingDisabled: true,
         },
         {
            name: 'creationDate',
            accessor: 'creationDate',
            formatter: dateFormatter,
            label: guardiansTranslationKeys.creationDate,
            width: columnsWidth.creationDate,
            sortingDisabled: true,
         },
         {
            name: 'actions',
            accessor: noop,

            renderer: ({ bean }: ValueRendererProps<GuardianSimple, any>) => (
               <ActionsCell onEdit={onEdit} onDelete={onDelete} guardian={bean} />
            ),
            label: '',
            width: columnsWidth.actions,
            sortingDisabled: true,
         },
      ];
   }, [onDelete, onEdit, translate]);
};
